import { useCallback, useEffect, useState } from 'react';
import { tagOverviewApi, type TagOverviewResponse } from '@/services/tagOverview';

type UseTagOverviewResult = {
  data: TagOverviewResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

export function useTagOverview(sheetId?: number | null, enabled = true): UseTagOverviewResult {
  const [data, setData] = useState<TagOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!enabled || sheetId == null) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const next = await tagOverviewApi.get(sheetId);
      setData(next);
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err : new Error('Failed to fetch tag overview'));
    } finally {
      setLoading(false);
    }
  }, [enabled, sheetId]);

  useEffect(() => {
    void load();
  }, [load]);

  return {
    data,
    loading,
    error,
    refetch: load,
  };
}

export default useTagOverview;
